import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import httpRequest from "../../../ajax";
import axios from "axios";
import Layout from "../../../layout";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import StarIcon from "@material-ui/icons/Star";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { CircularProgress } from "@material-ui/core";
import "./index.scss";

const JobMatchScore = () => {
  const [activeTab, setActiveTab] = useState("url");
  const [jobUrl, setJobUrl] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [error, setError] = useState(null);
  const { userData } = useSelector((store) => store.auth);

  // Fetch subscription data
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await httpRequest("get", "/job-seeker-subscriptions/my-subscription");
        if (response.data.status && response.data.data.subscription) {
          setSubscriptionData(response.data.data.subscription);
        }
      } catch (error) {
        console.log("No active subscription or error fetching:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscription();
  }, []);

  // Determine tier and credits
  const tier = subscriptionData?.tier || "freemium";
  const creditsRemaining = subscriptionData?.credits_remaining || 0;
  const planName = subscriptionData?.plan?.name || "Freemium";
  const isProHunter = tier === "pro_hunter";
  const isSprintPack = tier === "sprint_pack";
  const isFree = tier === "freemium" || !subscriptionData;

  // Determine what upsell to show
  let showUpsell = false;
  let upsellTier = "";
  let upsellMessage = "";
  
  if (isFree) {
    showUpsell = true;
    upsellTier = "SPRINT PACK";
    upsellMessage = "Get 10 Scan Credits";
  } else if (isSprintPack) {
    showUpsell = true;
    upsellTier = "PRO HUNTER";
    upsellMessage = "Unlock Unlimited Match Scans";
  }
  // Pro Hunter users see no upsell

  const handleAnalyze = async () => {
    if (!jobUrl && !jobDescription) {
      setError("Please enter a job URL or paste a job description");
      return;
    }

    // Check subscription access
    if (isFree) {
      setError(
        <span>
          Job Match Score is a premium feature.{" "}
          <a href="/my-subscriptions" style={{ color: "#991b1b", fontWeight: 700, textDecoration: "underline" }}>
            Subscribe to Sprint Pack or Pro Hunter
          </a>{" "}
          to access this feature.
        </span>
      );
      return;
    }

    if (isSprintPack && creditsRemaining <= 0) {
      setError(
        <span>
          You have no credits remaining.{" "}
          <a href="/my-subscriptions" style={{ color: "#991b1b", fontWeight: 700, textDecoration: "underline" }}>
            Upgrade to Pro Hunter
          </a>{" "}
          for unlimited scans or purchase more Sprint Pack credits.
        </span>
      );
      return;
    }

    setAnalyzing(true);
    setError(null);
    setMatchResults(null);

    try {
      const payload = {
        user_id: userData.id,
        include_culture_fit: false,
      };

      if (activeTab === "url") {
        payload.job_url = jobUrl;
      } else {
        payload.job_description = jobDescription;
      }

      const matchScoreApiUrl = process.env.REACT_APP_MATCH_SCORE_API_BASE_URL || "https://jobmatch.workbrook.com";
      const response = await axios.post(`${matchScoreApiUrl}/api/v1/get-match-score-external`, payload);
      
      if (response.data) {
        setMatchResults(response.data);
        setJobDescription("");
        
        // Deduct credit for Sprint Pack users (Pro Hunter is unlimited, no deduction needed)
        if (isSprintPack) {
          try {
            await httpRequest("post", "/job-seeker-subscriptions/use-credit", {
              feature: "job_match_score",
              job_id: null,
              metadata: { job_url: jobUrl || null, has_description: !!jobDescription }
            });
            // Refresh subscription data to update credits
            const subResponse = await httpRequest("get", "/job-seeker-subscriptions/my-subscription");
            if (subResponse.data.status && subResponse.data.data.subscription) {
              setSubscriptionData(subResponse.data.data.subscription);
            }
          } catch (creditErr) {
            console.error("Failed to deduct credit:", creditErr);
          }
        }
      }
    } catch (err) {
      console.error("Match score error:", err);
      setError(err.response?.data?.detail?.[0]?.msg || "Failed to analyze job match. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  // Show loading state while fetching subscription
  if (loading) {
    return (
      <Layout>
        <div className="jms-loading-container">
          <CircularProgress size={60} style={{ color: "#1d4ed8" }} />
          <p className="jms-loading-text">Loading your subscription details...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="jms-wrapper">
        <div className="jms-page">

        {/* ── Header ── */}
        <div className="jms-header">
          <h2 className="jms-title">Job Match Score</h2>
          <p className="jms-subtitle">Compare your CV against any live job posting instantly.</p>
          <p className="jms-pricing">
            {isFree && (
              <>
                Premium Analysis:{" "}
                <a href="/job-seeker-subscriptions">Get Sprint Pack (10 credits) or Pro Hunter (unlimited).</a>
              </>
            )}
            {isSprintPack && (
              <>
                Current Plan: <strong>Sprint Pack</strong> —{" "}
                <a href="/job-seeker-subscriptions">Upgrade to Pro Hunter for unlimited scans.</a>
              </>
            )}
            {isProHunter && (
              <>
                Current Plan: <strong style={{ color: "#1d4ed8" }}>Pro Hunter</strong> — Unlimited scans active!
              </>
            )}
          </p>
        </div>

        {/* ── Pro Upsell Banner ── */}
        {showUpsell && (
          <div 
            className="jms-pro-banner" 
            onClick={() => window.location.href = "/job-seeker-subscriptions"}
          >
            <div className="jms-pro-left">
              <div className="jms-pro-badge">
                <StarIcon style={{ fontSize: 11 }} />
                <span>{upsellTier}</span>
              </div>
              <div className="jms-pro-text">
                <span className="jms-pro-exclusive">EXCLUSIVE ACCESS</span>
                <span className="jms-pro-cta">{upsellMessage}</span>
              </div>
            </div>
            <ArrowForwardIosIcon className="jms-pro-arrow" style={{ fontSize: 15 }} />
          </div>
        )}

        {/* ── Tabs + Input Card ── */}
        <div className="jms-card">
          <div className="jms-custom-tabs">
            <div
              className={`jms-custom-tab ${activeTab === "url" ? "jms-custom-tab-active" : ""}`}
              onClick={() => setActiveTab("url")}
            >
              Paste Job URL
            </div>
            <div
              className={`jms-custom-tab ${activeTab === "description" ? "jms-custom-tab-active" : ""}`}
              onClick={() => setActiveTab("description")}
            >
              Paste Job Description
            </div>
          </div>

          <div className="jms-input-wrap">
            <label className="jms-input-label">
              {activeTab === "url" ? "ENTER JOB LINK" : "PASTE JOB DESCRIPTION"}
            </label>
            {activeTab === "url" ? (
              <input
                type="url"
                className="jms-url-input"
                placeholder="https://www.linkedin.com/jobs/view/..."
                value={jobUrl}
                onChange={(e) => setJobUrl(e.target.value)}
                disabled={analyzing}
              />
            ) : (
              <textarea
                className="jms-textarea"
                placeholder="Paste the full job description here..."
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={5}
                disabled={analyzing}
              />
            )}
          </div>

          {error && (
            <div className="jms-error-message">
              {error}
            </div>
          )}

          <button 
            className="jms-analyze-btn" 
            onClick={handleAnalyze}
            disabled={analyzing || (!jobUrl && !jobDescription)}
          >
            {analyzing ? (
              <>
                <CircularProgress size={17} style={{ color: "#fff", marginRight: 8 }} />
                Analyzing...
              </>
            ) : (
              <>
                <TrackChangesIcon style={{ fontSize: 17, marginRight: 8 }} />
                Match and Analyze
              </>
            )}
          </button>
        </div>

        {/* ── Match Results ── */}
        {matchResults && (
          <div className="jms-results-container">
            <div className="jms-results-header">
              <h3>Match Analysis Results</h3>
            </div>

            {/* Match Score Circle */}
            <div className="jms-score-circle">
              <div className="jms-circle-wrapper">
                <svg className="jms-circle-svg" viewBox="0 0 120 120">
                  <circle
                    className="jms-circle-bg"
                    cx="60"
                    cy="60"
                    r="54"
                  />
                  <circle
                    className="jms-circle-progress"
                    cx="60"
                    cy="60"
                    r="54"
                    style={{
                      strokeDasharray: `${(matchResults.match_score / 100) * 339.292} 339.292`,
                    }}
                  />
                </svg>
                <div className="jms-score-text">
                  <span className="jms-score-number">{matchResults.match_score}%</span>
                  <span className="jms-score-label">Match Score</span>
                </div>
              </div>
            </div>

            {/* Recommendation */}
            <div className="jms-recommendation">
              <h4>Recommendation</h4>
              <p>{matchResults.recommendation}</p>
            </div>

            {/* Skills Section */}
            {(matchResults.matched_skills?.length > 0 || matchResults.missing_skills?.length > 0) && (
              <div className="jms-skills-section">
                {matchResults.matched_skills?.length > 0 && (
                  <div className="jms-skills-matched">
                    <h4>✓ Matched Skills ({matchResults.matched_skills.length})</h4>
                    <div className="jms-skills-list">
                      {matchResults.matched_skills.map((skill, idx) => (
                        <span key={idx} className="jms-skill-tag jms-skill-matched">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {matchResults.missing_skills?.length > 0 && (
                  <div className="jms-skills-missing">
                    <h4>⚠ Missing Skills ({matchResults.missing_skills.length})</h4>
                    <div className="jms-skills-list">
                      {matchResults.missing_skills.map((skill, idx) => (
                        <span key={idx} className="jms-skill-tag jms-skill-missing">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Culture Fit */}
            {matchResults.culture_fit && Object.keys(matchResults.culture_fit).length > 0 && (
              <div className="jms-culture-fit">
                <h4>🎯 Culture Fit Analysis</h4>
                <div className="jms-culture-grid">
                  {Object.entries(matchResults.culture_fit).map(([key, value]) => (
                    <div key={key} className="jms-culture-item">
                      <span className="jms-culture-key">{key}</span>
                      <span className="jms-culture-value">{JSON.stringify(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Details */}
            {matchResults.details && Object.keys(matchResults.details).length > 0 && (
              <div className="jms-details">
                <h4>📋 Detailed Breakdown</h4>
                <div className="jms-details-grid">
                  {Object.entries(matchResults.details).map(([key, value]) => (
                    <div key={key} className="jms-detail-card">
                      <div className="jms-detail-header">
                        <span className="jms-detail-title">{key}</span>
                        {value?.score !== undefined && (
                          <span className="jms-detail-score">
                            {value.score}/{value.max || 100}
                          </span>
                        )}
                      </div>
                      {value?.reason && (
                        <p className="jms-detail-reason">{value.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Bottom Stats ── */}
        <div className="jms-stats-grid">
          <div className="jms-stat-card jms-stat-blue">
            <div className="jms-stat-icon">
              <AccountBalanceWalletIcon style={{ fontSize: 20 }} />
            </div>
            <div className="jms-stat-body">
              <span className="jms-stat-label">CURRENT PLAN</span>
              {isFree && (
                <>
                  <p className="jms-stat-value">Free Tier (No Credits)</p>
                  <a href="/my-subscriptions" className="jms-stat-link">
                    Subscribe to Start Scanning
                  </a>
                </>
              )}
              {isSprintPack && (
                <>
                  <p className="jms-stat-value">
                    Sprint Pack{subscriptionData?.plan?.price_ngn ? ` (₦${Number(subscriptionData.plan.price_ngn).toLocaleString()})` : ""}
                  </p>
                  <a href="/my-subscriptions" className="jms-stat-link">
                    Upgrade to Pro Hunter
                  </a>
                </>
              )}
              {isProHunter && (
                <>
                  <p className="jms-stat-value">
                    Pro Hunter{subscriptionData?.plan?.price_ngn ? ` (₦${Number(subscriptionData.plan.price_ngn).toLocaleString()}/mo)` : ""}
                  </p>
                  <a href="/my-subscriptions" className="jms-stat-link">
                    Manage Subscription
                  </a>
                </>
              )}
            </div>
          </div>

          <div className="jms-stat-card jms-stat-amber">
            <div className="jms-stat-icon">
              <EmojiEventsIcon style={{ fontSize: 20 }} />
            </div>
            <div className="jms-stat-body">
              <span className="jms-stat-label">SCAN CREDITS</span>
              {isFree && (
                <>
                  <p className="jms-stat-value">No credits available</p>
                  <p className="jms-stat-remaining">
                    Subscribe to get credits
                  </p>
                </>
              )}
              {isSprintPack && (
                <>
                  <p className="jms-stat-value">1 credit per scan</p>
                  <p className="jms-stat-remaining">
                    Credits remaining: <strong>{creditsRemaining}</strong>
                  </p>
                </>
              )}
              {isProHunter && (
                <>
                  <p className="jms-stat-value">Unlimited scans</p>
                  <p className="jms-stat-remaining">
                    <strong style={{ color: "#15803d" }}>∞ Unlimited</strong>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        </div>
      </div>
    </Layout>
  );
};

export default JobMatchScore;